'use client';

import {
  Box,
  Modal,
  Button,
  SystemIcon,
  Typography,
} from '@wishket/design-system';
import { twJoin } from 'tailwind-merge';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import NewsletterDialogInput from '../NewsletterDialogInput';
import { NewsletterDialogInputProps } from '../NewsletterDialogInput/NewsletterDialogInput';

import { Link } from '@/shared/Components';
import { PATH } from '@/shared/constants';
import { useAnimatedVirtualKeyboard, useDeviceCheck } from '@/shared/hooks';

interface NewsletterSubscriptionContentsProps {
  onSubmit: () => void;
  onClose: () => void;
  emailInputProps: NewsletterDialogInputProps;
  isButtonDisabled?: boolean;
  pathname?: string;
}
interface NewsletterSubscriptionDialogProps
  extends NewsletterSubscriptionContentsProps {
  isCurrentInfoModalOpen?: string;
  isSubscribeDialogOpen: boolean;
  isVisible: boolean;
}

const NewsletterSubscriptionContents = ({
  onSubmit,
  onClose,
  emailInputProps,
  isButtonDisabled,
  pathname,
}: NewsletterSubscriptionContentsProps) => {
  return (
    <>
      <Box
        data-testid="newsletter-subscribe-dialog--container"
        className="flex w-full items-center justify-center gap-6 desktop:justify-between"
      >
        <Typography
          variant="subTitle2"
          className="hidden text-w-gray-900 desktop:block"
        >
          요즘IT가 <strong>PICK</strong>한 뉴스레터를 받아보세요!
        </Typography>
        <Typography
          variant="subTitle1"
          className="flex flex-col whitespace-pre text-center text-w-gray-900 tablet:flex-row desktop:hidden"
        >
          <span>
            요즘IT가 <strong>PICK</strong>
            {'한 뉴스레터를 '}
          </span>
          <span>
            <strong>매주 목요일</strong>에 만나보세요.
          </span>
        </Typography>
        <button onClick={onClose} className="hidden desktop:block">
          <SystemIcon name="medium_arrow_down" />
        </button>
      </Box>
      <NewsletterDialogInput {...emailInputProps} />
      <Box className="flex flex-col gap-4">
        <Button
          data-testid="newsletter-subscribe-dialog--submit-button"
          variant="solid"
          className="w-full"
          onClick={onSubmit}
          disabled={isButtonDisabled}
        >
          무료로 구독하기
        </Button>
        <Typography
          variant="body1"
          className="inline-flex w-full justify-center text-w-gray-900"
        >
          <Link href={`${PATH.AUTH_LOGIN}?next=${pathname}`}>
            <span className="font-medium text-primary hover:underline">
              로그인
            </span>
          </Link>
          하고 그만보기
        </Typography>
      </Box>
    </>
  );
};

const NewsletterSubscriptionDialog = ({
  isVisible,
  emailInputProps,
  isButtonDisabled,
  isCurrentInfoModalOpen,
  isSubscribeDialogOpen,
  onClose,
  onSubmit,
}: NewsletterSubscriptionDialogProps) => {
  const pathname = usePathname();
  const { isDesktop, isLoading } = useDeviceCheck();
  const { keyboardHeight } = useAnimatedVirtualKeyboard();

  const [isBottomModalOpen, setIsBottomModalOpen] = useState(false);
  const [isReadyToBottomModalShow, setIsReadyToBottomModalShow] =
    useState(false);
  const isVisibleRef = useRef(isVisible);
  const isSubscribeDialogOpenRef = useRef(isSubscribeDialogOpen);

  useEffect(() => {
    isVisibleRef.current = isVisible;
    isSubscribeDialogOpenRef.current = isSubscribeDialogOpen;
  }, [isVisible, isSubscribeDialogOpen]);

  useEffect(() => {
    const scrollListener = () => {
      if (isVisibleRef.current && isSubscribeDialogOpenRef.current) {
        window.scrollTo(0, scrollY);
        setIsReadyToBottomModalShow(true);
      }
    };

    window.addEventListener('scroll', scrollListener, { passive: true });

    return () => {
      window.removeEventListener('scroll', scrollListener);
    };

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    requestAnimationFrame(() =>
      setIsBottomModalOpen(
        isSubscribeDialogOpen && isVisible && isReadyToBottomModalShow
      )
    );
  }, [isSubscribeDialogOpen, isVisible, isReadyToBottomModalShow]);

  const handleOnClose = () => {
    if (!isCurrentInfoModalOpen) {
      onClose();
    }
  };

  if (isLoading) return <></>;

  if (isDesktop) {
    return (
      <Box
        className={twJoin(
          'hidden desktop:flex desktop:flex-col',
          'absolute bottom-[calc(100%_+_24px)] right-0',
          'w-[440px] gap-6 rounded-3xl border border-w-gray-100 bg-w-white p-6 shadow-graylarge',
          'origin-[80%_105%] transition duration-100',
          isSubscribeDialogOpen ? 'scale-100' : 'scale-0'
        )}
      >
        <NewsletterSubscriptionContents
          pathname={pathname}
          emailInputProps={emailInputProps}
          isButtonDisabled={isButtonDisabled}
          onSubmit={onSubmit}
          onClose={handleOnClose}
        />
      </Box>
    );
  }

  return (
    <Modal
      rootId="portal"
      variant="bottom"
      isFixedHeight={false}
      isOpen={isBottomModalOpen}
      onClose={handleOnClose}
    >
      <Box style={{ paddingBottom: `${keyboardHeight}px` }}>
        <Box className="flex flex-col gap-6 px-5 pb-8 pt-5">
          <NewsletterSubscriptionContents
            pathname={pathname}
            emailInputProps={emailInputProps}
            isButtonDisabled={isButtonDisabled}
            onClose={handleOnClose}
            onSubmit={onSubmit}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default NewsletterSubscriptionDialog;
